import { Schema } from "effect"
import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "effect/http-api"
import { Authentication } from "./AuthApi.ts"
import { InstrumentId } from "./MarketData.ts"
import {
  AccountActive,
  AccountArchived,
  AccountBusy,
  AccountNotFound,
  ClientOrderIdConflict,
  IllegalRunnerCommand,
  InvalidAccountConfig,
  InvalidRunnerParams,
  NonDeterministic,
  NotReplayAccount,
  OrderDenied,
  OrderNotAmendable,
  OrderNotCancellable,
  OrderNotFound,
  PositionNotFound,
  ProductDisallowsShort,
  ReplayFinished,
  RunnerConflict,
  RunnerNotFound,
  UnknownInstrument,
  UnknownStrategy
} from "./Errors.ts"
import {
  AccountId,
  AccountSnapshot,
  AmendOrder,
  AccountSummary,
  AttachRunner,
  ClockCommand,
  ClockState,
  CreateAccount,
  RenameAccount,
  FillsPage,
  Order,
  OrderId,
  OrdersPage,
  PlaceOrder,
  Position,
  Runner,
  SetProtection,
  RunnerId,
  RunnerReport,
  SessionSummary,
  StopRunner
} from "./Money.ts"

// Paper trading accounts (docs/design/0005, P2 and P3). Named AccountsApi because
// TradingApi is the composed API in Api.ts. Every account belongs to one user;
// another user's account answers AccountNotFound.

/** 201: a new order. */
export const PlacedOrder = Schema.TaggedStruct("Placed", { order: Order }).pipe(HttpApiSchema.status(201))
/** 200: the same clientOrderId and payload were sent before; this is that order, unchanged. */
export const DuplicateOrder = Schema.TaggedStruct("Duplicate", { order: Order }).pipe(HttpApiSchema.status(200))
export type PlaceOrderResult = typeof PlacedOrder.Type | typeof DuplicateOrder.Type

/** open: orders that can still fill or be cancelled. closed: terminal ones. Omitted: all. */
export const OrderStatusFilter = Schema.Literals(["open", "closed"])
export type OrderStatusFilter = typeof OrderStatusFilter.Type

const PageQuery = {
  /** Only items with a smaller seq (the previous page's `nextBefore`). */
  before: Schema.optional(Schema.Int),
  /** Default 100, at most 500. */
  limit: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 500 }))))
}

const accountParams = { id: AccountId }
const orderParams = { id: AccountId, orderId: OrderId }
const positionParams = { id: AccountId, instrumentId: InstrumentId }
const runnerParams = { id: AccountId, runnerId: RunnerId }

export class AccountsApi extends HttpApiGroup.make("accounts")
  .add(
    // --- Accounts ---------------------------------------------------------------
    /** The caller's accounts, archived ones included, newest first. */
    HttpApiEndpoint.get("list", "/", { success: Schema.Array(AccountSummary) }),
    HttpApiEndpoint.post("create", "/", {
      payload: CreateAccount,
      success: AccountSummary.pipe(HttpApiSchema.status(201)),
      error: InvalidAccountConfig
    }),
    HttpApiEndpoint.get("get", "/:id", {
      params: accountParams,
      success: AccountSnapshot,
      error: AccountNotFound
    }),
    /** Renames the account (docs/design/0007). Archived accounts keep their name. */
    HttpApiEndpoint.patch("rename", "/:id", {
      params: accountParams,
      payload: RenameAccount,
      success: AccountSummary,
      error: [AccountNotFound, AccountArchived]
    }),
    /** Archives the account and returns its replacement (same name, currency, cash, clock and config; `resetOf` set). */
    HttpApiEndpoint.post("reset", "/:id/reset", {
      params: accountParams,
      success: AccountSummary.pipe(HttpApiSchema.status(201)),
      error: [AccountNotFound, AccountArchived, AccountBusy]
    }),
    /**
     * Ends the session (docs/design/0008): cancels open orders, stops runners (without
     * flattening), records the final marks, and archives the account. Positions stay open,
     * valued at those marks.
     */
    HttpApiEndpoint.post("end", "/:id/end", {
      params: accountParams,
      success: AccountSummary,
      error: [AccountNotFound, AccountArchived, AccountBusy]
    }),
    /**
     * Deletes an ended account for good (docs/design/0015): its journal, orders, fills and
     * runners go with it. A running account answers AccountActive: end its session first.
     */
    HttpApiEndpoint.delete("remove", "/:id", {
      params: accountParams,
      success: HttpApiSchema.NoContent,
      error: [AccountNotFound, AccountActive]
    }),
    /** How the session went: live while it runs, frozen at its final marks once ended. */
    HttpApiEndpoint.get("sessionSummary", "/:id/session-summary", {
      params: accountParams,
      success: SessionSummary,
      error: AccountNotFound
    }),

    // --- Orders and fills -------------------------------------------------------
    HttpApiEndpoint.get("listOrders", "/:id/orders", {
      params: accountParams,
      query: { status: Schema.optional(OrderStatusFilter), ...PageQuery },
      success: OrdersPage,
      error: AccountNotFound
    }),
    HttpApiEndpoint.get("listFills", "/:id/fills", {
      params: accountParams,
      query: PageQuery,
      success: FillsPage,
      error: AccountNotFound
    }),
    HttpApiEndpoint.post("placeOrder", "/:id/orders", {
      params: accountParams,
      payload: PlaceOrder,
      success: [PlacedOrder, DuplicateOrder],
      error: [AccountNotFound, AccountArchived, ClientOrderIdConflict, OrderDenied, AccountBusy]
    }),
    /** 202: the order is PendingCancel (paper confirms it at once, so it may already be Cancelled). */
    HttpApiEndpoint.post("cancelOrder", "/:id/orders/:orderId/cancel", {
      params: orderParams,
      success: Order.pipe(HttpApiSchema.status(202)),
      error: [AccountNotFound, AccountArchived, OrderNotFound, OrderNotCancellable, AccountBusy]
    }),
    /** Moves a working order's price in place (docs/design/0013): same order id. */
    HttpApiEndpoint.patch("amendOrder", "/:id/orders/:orderId", {
      params: orderParams,
      payload: AmendOrder,
      success: Order,
      error: [AccountNotFound, AccountArchived, OrderNotFound, OrderNotAmendable, OrderDenied, AccountBusy]
    }),
    /** Sets, moves or removes a position's stop-loss and take-profit (docs/design/0013). */
    HttpApiEndpoint.put("setProtection", "/:id/positions/:instrumentId/protection", {
      params: positionParams,
      payload: SetProtection,
      success: Position,
      error: [AccountNotFound, AccountArchived, PositionNotFound, OrderDenied, AccountBusy]
    }),

    // --- Replay clock -----------------------------------------------------------
    HttpApiEndpoint.post("clock", "/:id/clock", {
      params: accountParams,
      payload: ClockCommand,
      success: ClockState,
      // UnknownInstrument: an id in Play/Step `follow` isn't in the catalog.
      error: [AccountNotFound, AccountArchived, NotReplayAccount, ReplayFinished, AccountBusy, UnknownInstrument]
    }),

    // --- Strategy runners (P3) ----------------------------------------------------
    HttpApiEndpoint.get("listRunners", "/:id/runners", {
      params: accountParams,
      success: Schema.Array(Runner),
      error: AccountNotFound
    }),
    HttpApiEndpoint.post("attachRunner", "/:id/runners", {
      params: accountParams,
      payload: AttachRunner,
      success: Runner.pipe(HttpApiSchema.status(201)),
      error: [
        AccountNotFound,
        AccountArchived,
        UnknownInstrument,
        UnknownStrategy,
        RunnerConflict,
        ProductDisallowsShort,
        InvalidRunnerParams,
        NonDeterministic,
        AccountBusy
      ]
    }),
    HttpApiEndpoint.post("pauseRunner", "/:id/runners/:runnerId/pause", {
      params: runnerParams,
      success: Runner,
      error: [AccountNotFound, RunnerNotFound, IllegalRunnerCommand, AccountBusy]
    }),
    /** Resumes a Paused runner, or a Halted one while it is flat (re-anchoring it). */
    HttpApiEndpoint.post("resumeRunner", "/:id/runners/:runnerId/resume", {
      params: runnerParams,
      success: Runner,
      error: [AccountNotFound, RunnerNotFound, IllegalRunnerCommand, AccountBusy]
    }),
    HttpApiEndpoint.post("stopRunner", "/:id/runners/:runnerId/stop", {
      params: runnerParams,
      payload: StopRunner,
      success: Runner,
      error: [AccountNotFound, RunnerNotFound, IllegalRunnerCommand, AccountBusy]
    }),
    HttpApiEndpoint.get("runnerReport", "/:id/runners/:runnerId/report", {
      params: runnerParams,
      success: RunnerReport,
      error: [AccountNotFound, RunnerNotFound]
    })
  )
  .middleware(Authentication)
  .prefix("/accounts")
{}
